import axios, { type AxiosError } from "axios";
import type { ConfirmPageData } from "../types";

interface ConfirmErrorBody {
  status: "not_found" | "expired" | "revoked";
  message: string;
}

// Public confirmation endpoints don't need the authenticated apiClient
// (no session cookie required), but we still route through /api and let
// the browser attach the CSRF cookie set on GET automatically.
const publicClient = axios.create({ baseURL: "/api", withCredentials: true });

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

export interface ConfirmFetchResult {
  ok: boolean;
  data?: ConfirmPageData;
  errorMessage?: string;
}

export interface ConfirmDecision {
  studentId: string;
  decision: "ATTENDING" | "NOT_ATTENDING";
  reason?: string;
}

export async function fetchConfirmationDetails(token: string): Promise<ConfirmFetchResult> {
  try {
    const { data } = await publicClient.get<ConfirmPageData>(`/confirm/${token}`);
    return { ok: true, data };
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const axiosErr = err as AxiosError<ConfirmErrorBody>;
      if (axiosErr.response?.data?.message) {
        return { ok: false, errorMessage: axiosErr.response.data.message };
      }
    }
    return { ok: false, errorMessage: "Unable to load this confirmation link." };
  }
}

export async function submitConfirmation(token: string, decisions: ConfirmDecision[]): Promise<ConfirmPageData["children"]> {
  const csrfToken = readCookie("csrf_token");
  const { data } = await publicClient.post<{ children: ConfirmPageData["children"] }>(
    `/confirm/${token}`,
    { decisions },
    { headers: csrfToken ? { "x-csrf-token": csrfToken } : {} },
  );
  return data.children;
}
